import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Plus, Download, FileText, AlertCircle, Clock, CheckCircle2, 
  Edit2, Trash2, X, Bot, Search
} from 'lucide-react';
import { toast } from 'sonner';
import { projectAPI } from '../lib/api';

const statusOptions = [
  { value: 'pending', label: 'Pending', color: 'text-amber-400 bg-amber-500/10 border-amber-500/20' },
  { value: 'approved', label: 'Approved', color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20' },
  { value: 'rejected', label: 'Rejected', color: 'text-red-400 bg-red-500/10 border-red-500/20' },
  { value: 'implemented', label: 'Implemented', color: 'text-blue-400 bg-blue-500/10 border-blue-500/20' },
];

const priorityOptions = ['Low', 'Medium', 'High', 'Critical'];

export default function ChangeRequestsPanel({ projectId, canEdit = true }) {
  const [changeRequests, setChangeRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [aiTemplate, setAiTemplate] = useState('');
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [newCR, setNewCR] = useState({
    title: '',
    description: '',
    reason: '',
    impact: '',
    priority: 'Medium',
    status: 'pending',
    requested_by: '',
  });

  useEffect(() => {
    loadChangeRequests();
  }, [projectId]);

  const loadChangeRequests = async () => {
    try {
      const res = await projectAPI.getChangeRequests(projectId);
      setChangeRequests(res.data || []);
    } catch (err) {
      console.log('Loading change requests:', err);
      setChangeRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCR = async () => {
    if (!newCR.title.trim()) {
      toast.error('Title is required');
      return;
    }
    try {
      await projectAPI.addChangeRequest(projectId, newCR);
      setShowAddModal(false);
      setNewCR({ title: '', description: '', reason: '', impact: '', priority: 'Medium', status: 'pending', requested_by: '' });
      await loadChangeRequests();
      toast.success('Change request added');
    } catch (err) {
      toast.error('Failed to add change request');
    }
  };

  const handleUpdateStatus = async (crId, newStatus) => {
    try {
      await projectAPI.updateChangeRequest(projectId, crId, { status: newStatus });
      await loadChangeRequests();
      toast.success('Status updated');
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (crId) => {
    try {
      await projectAPI.deleteChangeRequest(projectId, crId);
      await loadChangeRequests();
      toast.success('Deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const downloadAsWord = (cr) => {
    let content = aiTemplate || `# Change Request: ${cr.title}

## Details
- **ID:** CR-${cr.id?.slice(-6) || '000'}
- **Status:** ${cr.status}
- **Priority:** ${cr.priority}
- **Requested By:** ${cr.requested_by || 'N/A'}
- **Date:** ${new Date(cr.created_at).toLocaleDateString()}

## Description
${cr.description || 'No description provided.'}

## Reason for Change
${cr.reason || 'No reason specified.'}

## Impact Analysis
${cr.impact || 'No impact analysis provided.'}
`;

    // Apply template variables
    content = content
      .replace(/\{title\}/g, cr.title)
      .replace(/\{status\}/g, cr.status)
      .replace(/\{priority\}/g, cr.priority)
      .replace(/\{description\}/g, cr.description || '')
      .replace(/\{reason\}/g, cr.reason || '')
      .replace(/\{impact\}/g, cr.impact || '')
      .replace(/\{requested_by\}/g, cr.requested_by || '')
      .replace(/\{date\}/g, new Date(cr.created_at).toLocaleDateString());

    const blob = new Blob([content], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `CR-${cr.title.replace(/\s+/g, '_')}.doc`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredCRs = changeRequests.filter(cr => {
    const matchesSearch = cr.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          cr.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || cr.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const opt = statusOptions.find(s => s.value === status) || statusOptions[0];
    return <Badge className={opt.color}>{opt.label}</Badge>;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[var(--accent-indigo)] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="h-full flex flex-col" data-testid="change-requests-panel">
      {/* Header */}
      <div className="p-4 border-b border-[var(--surface-border)]">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-semibold text-left">Change Requests</h2>
            <p className="text-xs opacity-60 text-left">Track and manage project change requests</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowTemplateModal(true)}>
              <Bot className="w-3.5 h-3.5 mr-1.5" /> Template
            </Button>
            {canEdit && (
              <Button size="sm" onClick={() => setShowAddModal(true)}>
                <Plus className="w-3.5 h-3.5 mr-1.5" /> New CR
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 opacity-40" />
            <Input
              placeholder="Search change requests..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 h-8 text-sm"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[140px] h-8">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {statusOptions.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* List */}
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-3">
          {filteredCRs.length === 0 ? (
            <div className="text-center py-12 opacity-60">
              <AlertCircle className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No change requests found</p>
              {canEdit && <p className="text-xs mt-1">Click "New CR" to add one</p>}
            </div>
          ) : (
            filteredCRs.map((cr) => (
              <div
                key={cr.id}
                className="p-4 rounded-lg border border-[var(--surface-border)] bg-[var(--surface-card)] hover:border-[var(--accent-indigo)]/30 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono opacity-50">CR-{cr.id?.slice(-6)}</span>
                      {getStatusBadge(cr.status)}
                      <Badge variant="outline" className="text-[10px]">{cr.priority}</Badge>
                    </div>
                    <h3 className="font-medium text-left">{cr.title}</h3>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => downloadAsWord(cr)}>
                      <Download className="w-3.5 h-3.5" />
                    </Button>
                    {canEdit && (
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={() => handleDelete(cr.id)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
                {cr.description && (
                  <p className="text-sm opacity-70 mb-2 text-left">{cr.description}</p>
                )}
                <div className="flex items-center justify-between text-xs opacity-50">
                  <span>By: {cr.requested_by || 'Unknown'}</span>
                  <span>{new Date(cr.created_at).toLocaleDateString()}</span>
                </div>
                {canEdit && (
                  <div className="mt-3 pt-3 border-t border-[var(--surface-border)] flex gap-2">
                    {statusOptions.map(s => (
                      <Button
                        key={s.value}
                        variant={cr.status === s.value ? 'default' : 'outline'}
                        size="sm"
                        className="text-xs h-7"
                        onClick={() => handleUpdateStatus(cr.id, s.value)}
                      >
                        {s.label}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </ScrollArea>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[var(--surface-card)] rounded-xl border border-[var(--surface-border)] w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">New Change Request</h3>
              <Button variant="ghost" size="icon" onClick={() => setShowAddModal(false)}><X className="w-4 h-4" /></Button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium mb-1 block text-left">Title *</label>
                <Input value={newCR.title} onChange={(e) => setNewCR({ ...newCR, title: e.target.value })} placeholder="Brief title for the change request" />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block text-left">Description</label>
                <Textarea value={newCR.description} onChange={(e) => setNewCR({ ...newCR, description: e.target.value })} placeholder="Detailed description of the change" rows={3} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium mb-1 block text-left">Reason for Change</label>
                  <Textarea value={newCR.reason} onChange={(e) => setNewCR({ ...newCR, reason: e.target.value })} placeholder="Why is this change needed?" rows={2} />
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block text-left">Impact</label>
                  <Textarea value={newCR.impact} onChange={(e) => setNewCR({ ...newCR, impact: e.target.value })} placeholder="Impact on project scope/timeline" rows={2} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium mb-1 block text-left">Priority</label>
                  <Select value={newCR.priority} onValueChange={(v) => setNewCR({ ...newCR, priority: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {priorityOptions.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block text-left">Requested By</label>
                  <Input value={newCR.requested_by} onChange={(e) => setNewCR({ ...newCR, requested_by: e.target.value })} placeholder="Name" />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
              <Button onClick={handleAddCR}>Add Change Request</Button>
            </div>
          </div>
        </div>
      )}

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[var(--surface-card)] rounded-xl border border-[var(--surface-border)] w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Word Export Template</h3>
              <Button variant="ghost" size="icon" onClick={() => setShowTemplateModal(false)}><X className="w-4 h-4" /></Button>
            </div>
            <p className="text-sm opacity-70 mb-3 text-left">
              Customize the template for Word exports. Use variables: {'{title}'}, {'{status}'}, {'{priority}'}, {'{description}'}, {'{reason}'}, {'{impact}'}, {'{requested_by}'}, {'{date}'}
            </p>
            <Textarea
              value={aiTemplate}
              onChange={(e) => setAiTemplate(e.target.value)}
              placeholder="Leave empty for default template. Or paste your custom template here..."
              rows={10}
              className="font-mono text-xs"
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => { setAiTemplate(''); setShowTemplateModal(false); }}>Reset</Button>
              <Button onClick={() => setShowTemplateModal(false)}>Save Template</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
